/** A chat's title from what the customer wrote (Htet, 2026-10-12). The API
 *  does not summarise, so a typed first message used to name the chat word
 *  for word ("Hi I want to ask for eSIM replacement"). The title keeps what
 *  the chat is about ("eSIM replacement"): the first sentence that says
 *  something, without the greeting (every site language, plus Portuguese for
 *  the whitelabel shops), pleasantries and
 *  the English lead-ins ("I want to ask for", "Can you help me with"), first
 *  letter capitalised, 80 characters. "" when nothing is left ("Hi", "I have
 *  a question"). Used when a typed first message names a new chat and when a
 *  title is shown, so chats named before read the same. A common subject
 *  the message mentions names the chat first (chatSubjectOf, below). Later
 *  the backend may let Ben name the chat; this stays the fallback. */

const GREETINGS = [
  // English
  "good morning",
  "good afternoon",
  "good evening",
  "good day",
  "greetings",
  "hello",
  "hiya",
  "howdy",
  "hey",
  "hi",
  "dear",
  // German
  "guten morgen",
  "guten tag",
  "guten abend",
  "liebes team",
  "hallo",
  "servus",
  "moin",
  "grüezi",
  // Italian
  "buongiorno",
  "buon pomeriggio",
  "buonasera",
  "salve",
  "ciao",
  // French
  "bonjour",
  "bonsoir",
  "salut",
  "coucou",
  // Spanish
  "buenos días",
  "buenos dias",
  "buenas tardes",
  "buenas noches",
  "buenas",
  "hola",
  "saludos",
  // Portuguese
  "bom dia",
  "boa tarde",
  "boa noite",
  "prezados",
  "olá",
  "ola",
  "oi",
  // Russian
  "здравствуйте",
  "здравствуй",
  "добрый день",
  "добрый вечер",
  "доброе утро",
  "приветствую",
  "привет",
  // Turkish
  "merhabalar",
  "merhaba",
  "selamlar",
  "selam",
  "iyi günler",
  "iyi akşamlar",
  "günaydın",
  // Arabic
  "السلام عليكم",
  "صباح الخير",
  "مساء الخير",
  "مرحباً",
  "مرحبا",
  "أهلاً",
  "أهلا",
  "اهلا",
  // Korean
  "안녕하십니까",
  "안녕하세요",
  "안녕",
];

/** Japanese and Chinese run words together, so these are dropped even when
 *  the text follows without a space. */
const RUN_ON_GREETINGS = [
  "お世話になっております",
  "お世話になります",
  "おはようございます",
  "こんにちは",
  "こんばんは",
  "はじめまして",
  "すみません",
  "大家好",
  "早上好",
  "下午好",
  "晚上好",
  "您好",
  "你好",
  "哈喽",
  "哈囉",
  "嗨",
];

/** Who a greeting is addressed to: "Hi team", "Hello BNESIM support". */
const ADDRESSEES = [
  "there",
  "team",
  "all",
  "everyone",
  "everybody",
  "guys",
  "folks",
  "support",
  "bnesim",
  "ben",
  // The brand's own name on the whitelabel shops ("Hi Kaspersky team").
  ...(process.env.GATSBY_APP_NAME ?? "")
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word.length > 2 && word !== "esim"),
  "sir",
  "madam",
  "zusammen",
  "a tutti",
  "à tous",
  "a todos",
];

const PLEASANTRIES = [
  "sorry to bother you",
  "sorry for the trouble",
  "i hope you're doing well",
  "i hope you are doing well",
  "i hope you're well",
  "i hope you are well",
  "hope you're doing well",
  "hope you are doing well",
  "hope you're well",
  "hope you are well",
  "how are you doing",
  "how are you",
  "thanks in advance",
  "excuse me",
  "sorry",
  "please",
  "pls",
  "plz",
];

const SIGN_OFFS = [
  "thanks in advance",
  "thank you",
  "thanks",
  "thx",
  "ty",
  "please",
  "pls",
  "plz",
];

/** Longest first, so "hello" wins over "hi" and "안녕하세요" over "안녕". */
const anyOf = (phrases: string[]): string =>
  [...phrases]
    .sort((a, b) => b.length - a.length)
    .map(phrase => phrase.replace(/ /g, "\\s+"))
    .join("|");

/** Not followed by a letter, so "hi" never takes the start of "his". */
const WORD_END = "(?![\\p{L}\\p{M}\\p{N}'’])";

const GREETING = new RegExp(
  `^(?:(?:${anyOf(GREETINGS)})${WORD_END}|${anyOf(RUN_ON_GREETINGS)})` +
    `(?:[\\s,]+(?:${anyOf(ADDRESSEES)})${WORD_END})*`,
  "iu",
);
const PLEASANTRY = new RegExp(`^(?:${anyOf(PLEASANTRIES)})${WORD_END}`, "iu");
/** Spaces, punctuation (Arabic and CJK too) and emoji between the greeting
 *  and the question. */
const JUNK =
  "\\s\\p{Extended_Pictographic}\\u{FE0F}\\u{200D},.;:~\\-–—、。，！・،؛";
// The class lists U+FE0F and U+200D on their own, not as a joined emoji.
// eslint-disable-next-line no-misleading-character-class
const SEPARATORS = new RegExp(`^[${JUNK}!?？؟]+`, "u");
const SIGN_OFF = new RegExp(
  `(?:[\\s,،]+(?:${anyOf(SIGN_OFFS)}))*[${JUNK}!…]*$`,
  "iu",
);
const LIST_MARKER = /^\s*(?:[-*•]|\d+[.)])\s+/;

const LEAD_INS: RegExp[] = [
  // "I want to ask for eSIM replacement", "I'd like to know how …"
  /^(?:i\s+(?:would\s+like|want(?:ed)?|wish|need)|i['’]d\s+like)\s+to\s+(?:ask|know|check|enquire|inquire|request|find\s+out)(?:\s+you)?(?:\s+(?:about|for|regarding|concerning|on))?\s+/i,
  // "I have a problem with …" → "Problem with …"
  /^i\s+(?:have|had|got|['’]ve\s+got)\s+(?:a|an|some|one)\s+(?=(?:question|problem|issue|query|inquiry|enquiry|doubt)s?\b)/i,
  // "I need help with …" → "Help with …"
  /^(?:i\s+(?:need|want|would\s+like)|i['’]d\s+like)\s+(?:some\s+)?(?=help\b)/i,
  // "Can you help me with …" → "Help me with …"
  /^(?:can|could|would|will)\s+(?:you|u|someone|somebody|anyone)\s+(?:please\s+|pls\s+)?(?=help\b)/i,
  // "I'm writing about …"
  /^i(?:['’]m|\s+am)\s+(?:writing|contacting\s+you|reaching\s+out|messaging(?:\s+you)?)\s+(?:about|regarding|concerning|because\s+of|for|on)\s+/i,
  // "Can I ask about …"
  /^(?:can|could|may)\s+i\s+ask\s+(?:about|for|regarding)\s+/i,
];

/** A sentence that only opens the conversation. */
const FILLER =
  /^(?:(?:a|an|some|one)\s+)?(?:question|problem|issue|query|inquiry|enquiry|doubt)s?$|^help(?:\s+me)?$|^(?:something|support)$/i;

const SENTENCE_END = /[.!?؟]+(?=\s)|[。！？]+/g;

/** The message's lines, each split into sentences. */
const sentencesOf = (text: string): string[] =>
  text.split("\n").flatMap(line => {
    const sentences: string[] = [];
    let start = 0;
    SENTENCE_END.lastIndex = 0;
    for (
      let match = SENTENCE_END.exec(line);
      match !== null;
      match = SENTENCE_END.exec(line)
    ) {
      sentences.push(line.slice(start, match.index + match[0].length));
      start = match.index + match[0].length;
    }
    sentences.push(line.slice(start));
    return sentences;
  });

const tidy = (sentence: string): string => {
  let text = sentence.replace(LIST_MARKER, "").trim();
  let before;
  do {
    before = text;
    text = text
      .replace(GREETING, "")
      .replace(PLEASANTRY, "")
      .replace(SEPARATORS, "");
  } while (text !== before);
  for (const leadIn of LEAD_INS) text = text.replace(leadIn, "");
  text = text.replace(SIGN_OFF, "");
  return FILLER.test(text) ? "" : text;
};

/** "my eSIM" → "My eSIM"; a first word with capitals ("eSIM", "iPhone")
 *  stays as written. */
const capitalise = (text: string, locale: string | undefined): string => {
  const firstWord = text.split(/\s/)[0];
  if (firstWord !== firstWord.toLowerCase()) return text;
  const [first = "", ...rest] = Array.from(text);
  return (
    first.toLocaleUpperCase(locale === "kr" ? "ko" : locale) + rest.join("")
  );
};

export const chatTitleOf = (
  text: string | null | undefined,
  locale?: string,
): string => {
  for (const sentence of sentencesOf((text ?? "").trim())) {
    const title = tidy(sentence);
    if (title !== "") {
      return Array.from(capitalise(title, locale)).slice(0, 80).join("").trim();
    }
  }
  return "";
};

/** Common subjects, named by a fixed title wherever the message mentions
 *  them (Htet, 2026-10-12, until Ben names chats): "Hi is there a way to get
 *  refund" → "Refund". `words` start a word in any language that spaces its
 *  words, with any ending ("refund" finds "refunded"); `parts` are found
 *  anywhere (Arabic, which joins "al-" and "wa-" to the word, Chinese,
 *  Japanese). The subject mentioned first wins. */
const SUBJECTS: Array<{ key: string; words: string[]; parts: string[] }> = [
  {
    key: "chat_title_refund",
    words: [
      "refund",
      "money back",
      "reimburs",
      "chargeback",
      "rückerstattung",
      "erstattung",
      "geld zurück",
      "rückzahlung",
      "rimbors",
      "rembours",
      "reembols",
      "devolución",
      "devolucion",
      "devolução",
      "devolucao",
      "estorno",
      "возврат",
      "вернуть деньги",
      "верните деньги",
      "iade",
      "geri ödeme",
      "환불",
    ],
    parts: [
      "استرداد",
      "استرجاع",
      "رد المبلغ",
      "إرجاع المبلغ",
      "ارجاع المبلغ",
      "返金",
      "払い戻し",
      "退款",
      "退钱",
      "退錢",
      "退費",
    ],
  },
  {
    key: "chat_title_replacement",
    words: [
      "replac",
      "reissue",
      "re-issue",
      "new qr",
      "ersatz",
      "ersetz",
      "neuen qr",
      "neuer qr",
      "sostitu",
      "nuovo qr",
      "remplac",
      "nouveau qr",
      "reemplaz",
      "sustitu",
      "nuevo qr",
      "substitu",
      "troca",
      "novo qr",
      "замен",
      "новый qr",
      "yeni qr",
      "교체",
      "재발급",
    ],
    parts: [
      "استبدال",
      "شريحة جديدة",
      "再発行",
      "交換",
      "更换",
      "更換",
      "补发",
      "補發",
    ],
  },
  {
    key: "chat_title_top_up",
    words: [
      "top up",
      "recharg",
      "more data",
      "extra data",
      "add data",
      "additional data",
      "aufladen",
      "aufladung",
      "nachladen",
      "mehr daten",
      "ricaric",
      "più dati",
      "piu dati",
      "plus de données",
      "recarg",
      "más datos",
      "mas datos",
      "mais dados",
      "пополн",
      "докупить",
      "ek paket",
      "ek data",
      "ek internet",
      "paket yükle",
      "internet yükle",
      "충전",
      "데이터 추가",
    ],
    parts: [
      "إعادة الشحن",
      "اعادة الشحن",
      "شحن الباقة",
      "بيانات إضافية",
      "チャージ",
      "データ追加",
      "追加データ",
      "充值",
      "加流量",
      "增加流量",
    ],
  },
  {
    key: "chat_topic_setup",
    words: [
      "instal",
      "set up",
      "activat",
      "qr",
      "einricht",
      "aktivier",
      "configur",
      "attivar",
      "attivazione",
      "activer",
      "activar",
      "activación",
      "activacion",
      "ativar",
      "ativação",
      "ativacao",
      "настро",
      "установ",
      "активир",
      "kurulum",
      "kuramı",
      "kuramadı",
      "etkinleştir",
      "설치",
      "설정",
      "활성화",
    ],
    parts: [
      "تثبيت",
      "تفعيل",
      "إعداد",
      "اعداد",
      "インストール",
      "設定",
      "有効化",
      "アクティベート",
      "安装",
      "设置",
      "設置",
      "激活",
      "二维码",
    ],
  },
  {
    key: "chat_title_no_signal",
    words: [
      "no signal",
      "no service",
      "no internet",
      "no data",
      "no connection",
      "no network",
      "not work",
      "not connect",
      "cannot connect",
      "can't connect",
      "doesn't work",
      "don't work",
      "isn't work",
      "kein signal",
      "kein netz",
      "kein internet",
      "keine daten",
      "keine verbindung",
      "kein empfang",
      "funktioniert nicht",
      "geht nicht",
      "nessun segnale",
      "niente segnale",
      "senza segnale",
      "non funziona",
      "niente internet",
      "senza internet",
      "nessuna connessione",
      "pas de signal",
      "pas de réseau",
      "pas de reseau",
      "pas d'internet",
      "pas de connexion",
      "aucun réseau",
      "aucun signal",
      "fonctionne pas",
      "marche pas",
      "sin señal",
      "sin senal",
      "sin internet",
      "sin datos",
      "sin conexión",
      "sin conexion",
      "sin servicio",
      "no funciona",
      "no me funciona",
      "sem sinal",
      "sem internet",
      "sem dados",
      "sem conexão",
      "sem conexao",
      "sem serviço",
      "não funciona",
      "nao funciona",
      "нет сигнала",
      "нет сети",
      "нет интернета",
      "нет связи",
      "не работает",
      "не подключается",
      "sinyal yok",
      "internet yok",
      "çekmiyor",
      "bağlanmıyor",
      "çalışmıyor",
      "신호",
      "인터넷이 안",
      "인터넷 안",
      "작동하지",
      "연결이 안",
    ],
    parts: [
      "لا توجد إشارة",
      "لا يوجد إنترنت",
      "لا يوجد انترنت",
      "لا يوجد اتصال",
      "لا توجد شبكة",
      "لا يعمل",
      "لا تعمل",
      "圏外",
      "電波",
      "つながらない",
      "繋がらない",
      "つながりません",
      "繋がりません",
      "使えない",
      "使えません",
      "接続できない",
      "接続できません",
      "没有信号",
      "沒有信號",
      "无法上网",
      "無法上網",
      "不能上网",
      "上不了网",
      "没网",
      "連不上",
      "连不上",
      "无法使用",
      "用不了",
    ],
  },
];

const SUBJECT_MATCHERS = SUBJECTS.map(({ key, words, parts }) => ({
  key,
  // Spaces also match a hyphen or nothing: "top up", "top-up", "topup".
  words: new RegExp(
    `(?:^|[^\\p{L}\\p{N}])(?:${words.map(word => word.replace(/ /g, "[\\s-]*")).join("|")})`,
    "iu",
  ),
  parts,
}));

/** The translation key of the first common subject the message mentions. */
export const chatSubjectOf = (
  text: string | null | undefined,
): string | undefined => {
  const plain = (text ?? "")
    .replace(/[‘’]/g, "'")
    .replace(/İ/g, "i")
    .toLowerCase();
  let first: { key: string; at: number } | undefined;
  for (const { key, words, parts } of SUBJECT_MATCHERS) {
    const at = Math.min(
      ...[plain.search(words), ...parts.map(part => plain.indexOf(part))].map(
        found => (found < 0 ? Infinity : found),
      ),
    );
    if (at !== Infinity && (first === undefined || at < first.at)) {
      first = { key, at };
    }
  }
  return first?.key;
};
